import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

/**
 * プレビュー右下に表示する操作キーガイド。
 *
 * はじめて遊ぶ学習者にも移動方法がわかるよう、キーキャップ風の表示で
 * W/A/S/D・Space の役割（ひらがな）を常時示す。
 * 各キーはタップ/クリックでも操作できる（タブレット等のキーボードが
 * 使えない環境向け）。押している間だけ対応キーの入力として扱う。
 * ガイドの余白はポインタイベントを透過し、ビューポート操作を妨げない。
 */

interface KeyCapSpec {
  /** キーキャップに表示するキー名。 */
  readonly label: string
  /** キーの役割（ひらがな・キャップ上部に小さく表示）。 */
  readonly reading: string
  /** タップ時に送る KeyboardEvent.code 相当のキーコード。 */
  readonly code: string
}

export interface KeyGuideProps {
  /**
   * キーのタップ/リリースを仮想キー入力として通知する。
   * pressed=true が押下、false が解放。code は KeyboardEvent.code 相当。
   */
  onVirtualKey?: (code: string, pressed: boolean) => void
  /**
   * キーの役割表示。'drive'（車の運転中）は前進/バック/ブレーキの読みに
   * 切り替える（キーコード自体は同じ）。省略時は 'walk'。
   */
  mode?: 'walk' | 'drive'
}

/** 全キーキャップ共通の横幅（px）。横並びの見た目を揃える。 */
const CAP_WIDTH = 48

/** W キーの役割（モード別の読み）。 */
const TOP_READING: Record<'walk' | 'drive', string> = {
  walk: 'まえ',
  drive: 'すすむ',
}

/** 下段のキー（左から Space / A / S / D）。モード別の読みで組む。 */
const bottomRow = (mode: 'walk' | 'drive'): ReadonlyArray<KeyCapSpec> => [
  {
    label: 'Space',
    reading: mode === 'drive' ? 'ブレーキ' : 'ジャンプ',
    code: 'Space',
  },
  { label: 'A', reading: 'ひだり', code: 'KeyA' },
  { label: 'S', reading: mode === 'drive' ? 'バック' : 'うしろ', code: 'KeyS' },
  { label: 'D', reading: 'みぎ', code: 'KeyD' },
]

const guideStyle: CSSProperties = {
  position: 'absolute',
  right: 10,
  bottom: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 4,
  // 余白は透過し、下のビューポート操作（クリック/ドラッグ）を妨げない。
  // キーキャップ自身は capStyle 側で pointerEvents を有効化する。
  pointerEvents: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
  zIndex: 10,
}

const rowStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
}

const capStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: CAP_WIDTH,
  boxSizing: 'border-box',
  padding: '3px 6px',
  borderRadius: 8,
  background: 'rgba(255, 255, 255, 0.92)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.35)',
  color: '#111',
  lineHeight: 1.1,
  // タップ操作を受ける（ガイド全体の pointerEvents:none を上書き）。
  pointerEvents: 'auto',
  // タブレットでの長押し選択・スクロール・ダブルタップ拡大を抑止する。
  touchAction: 'none',
  // iOS Safari はドラッグ/長押しでラベル文字を選択・コールアウト表示するため、
  // キーキャップとその文字（子 span が継承）で選択とコールアウトを無効化する。
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
  cursor: 'pointer',
}

const capPressedStyle: CSSProperties = {
  background: 'rgba(255, 224, 130, 0.95)',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.35)',
  transform: 'translateY(1px)',
}

const readingStyle: CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
}

const labelStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  fontFamily: 'monospace',
}

const KeyCap = ({
  spec,
  onVirtualKey,
}: {
  spec: KeyCapSpec
  onVirtualKey?: (code: string, pressed: boolean) => void
}) => {
  const [pressed, setPressed] = useState(false)
  // 押下中のポインタ ID。同じキャップへの 2 本目以降の指は無視し、
  // 押した指の解放だけでキーを離す（マルチタッチの混線防止）。
  const activePointerId = useRef<number | null>(null)
  // ポインタキャプチャが取れなかった場合の解放漏れ対策として、押下中だけ
  // window の pointerup/pointercancel を購読する。PIP 窓でも動くよう、
  // 購読先 window はイベント発生元の ownerDocument から取る。
  const windowFallback = useRef<{
    win: Window
    handler: (event: PointerEvent) => void
  } | null>(null)
  // unmount（停止やシーン切替によるガイド除去）時に押しっぱなしを残さない
  // よう、最新の解放処理を ref 経由で呼べるようにする。
  const releaseActiveRef = useRef<() => void>(() => {})

  const releaseActive = useCallback((): void => {
    if (activePointerId.current === null) {
      return
    }
    activePointerId.current = null
    if (windowFallback.current) {
      const { win, handler } = windowFallback.current
      win.removeEventListener('pointerup', handler)
      win.removeEventListener('pointercancel', handler)
      windowFallback.current = null
    }
    setPressed(false)
    onVirtualKey?.(spec.code, false)
  }, [onVirtualKey, spec.code])
  releaseActiveRef.current = releaseActive
  useEffect(() => () => releaseActiveRef.current(), [])

  const press = (event: React.PointerEvent<HTMLDivElement>): void => {
    // ビューポートへのフォーカス移動やドラッグ開始を抑止する。
    event.preventDefault()
    event.stopPropagation()
    if (activePointerId.current !== null) {
      return // 既に別の指が押している
    }
    // 指がキャップ外へ滑っても pointerup を受け取れるようにする
    // （マルチタッチでも pointerId 単位で独立に動く）。capture が取れない
    // 環境でも押下自体は成立させ、解放は window フォールバックで拾う。
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // pointerId が既に無効な場合など。解放は window フォールバックと
      // unmount 時の解放で回収される。
    }
    activePointerId.current = event.pointerId
    const pointerId = event.pointerId
    const win = event.currentTarget.ownerDocument.defaultView
    if (win) {
      const handler = (windowEvent: PointerEvent): void => {
        if (windowEvent.pointerId === pointerId) {
          releaseActiveRef.current()
        }
      }
      windowFallback.current = { win, handler }
      win.addEventListener('pointerup', handler)
      win.addEventListener('pointercancel', handler)
    }
    setPressed(true)
    onVirtualKey?.(spec.code, true)
  }

  const release = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (activePointerId.current !== event.pointerId) {
      return
    }
    event.preventDefault()
    releaseActive()
  }

  return (
    <div
      style={pressed ? { ...capStyle, ...capPressedStyle } : capStyle}
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      // 右クリック等のコンテキストメニューで押しっぱなしにならないようにする。
      onContextMenu={(event) => event.preventDefault()}
    >
      <span style={readingStyle}>{spec.reading}</span>
      {/* 複数文字のラベル（Space）も統一幅へ収まるよう文字数で縮小する。 */}
      <span
        style={{ ...labelStyle, fontSize: spec.label.length > 1 ? 12 : 16 }}
      >
        {spec.label}
      </span>
    </div>
  )
}

export const KeyGuide = ({ onVirtualKey, mode = 'walk' }: KeyGuideProps) => (
  // ポインタ専用の補助 UI（キーボード利用者は実キーで同じ操作ができる）。
  // role=button を名乗るとキーボード操作経路の実装が ARIA 上必須になるため、
  // 支援技術からは隠す。
  <div style={guideStyle} aria-hidden="true">
    {/* 上段: W（まえ/すすむ）。下段の S の真上に揃える（右端から D の幅 + gap）。 */}
    <div style={{ ...rowStyle, marginRight: CAP_WIDTH + 4 }}>
      <KeyCap
        spec={{ label: 'W', reading: TOP_READING[mode], code: 'KeyW' }}
        onVirtualKey={onVirtualKey}
      />
    </div>
    <div style={rowStyle}>
      {bottomRow(mode).map((cap) => (
        <KeyCap key={cap.label} spec={cap} onVirtualKey={onVirtualKey} />
      ))}
    </div>
  </div>
)
